import React, { useState } from "react";
import moment from "moment";
import { CalendarDays, ChevronDown, ChevronUp, IndianRupee, MapPin } from "lucide-react";
import Cross from "sushiweb/Icons/all/Cross";
import BackButton from "../../Button/BackButton";
import { BottomModal, Button, FlexColumn, SushiModal } from "../../Common/styles.common";
import { buildGoogleMapsUrl } from "utils/mapUtils";
import styles from "./PurchaseOrderDetails.module.scss";

export const formatAmount = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export const formatDate = (date) =>
  date && moment(date).isValid() ? moment(date).format("DD MMM 'YY") : "—";

export const formatWeight = (weight) => {
  const value = Number(weight || 0);
  return value > 999
    ? `${(value / 1000).toFixed(2)} tonne`
    : `${value.toLocaleString("en-IN", { maximumFractionDigits: 2 })} kg`;
};

export const PurchaseOrderHeader = ({ number, summary, backPath, onBack, actions = [] }) => (
  <header className={styles.header}>
    <BackButton pathName={backPath} onClick={onBack} variant="header" />
    <div className={styles.headerTitle}>
      <h1 className={`fs-16 fw-700 ${styles.purchaseOrderNumber}`}>{number}</h1>
      <p className={`fs-12 ${styles.purchaseOrderMeta}`}>{summary}</p>
    </div>
    <div className={`d-flex ${styles.headerActions}`}>
      {actions.map((action) => (
        <button key={action.key} type="button" className={styles.iconButton} aria-label={action.label} onClick={action.onClick}>
          {action.icon}
        </button>
      ))}
    </div>
  </header>
);

export const PurchaseOrderButton = ({ outline, secondary, className = "", ...props }) => (
  <button
    {...props}
    className={`${styles.button} ${outline ? styles.outlineButton : ""} ${secondary ? styles.secondaryButton : ""} ${className}`}
  />
);

export const PurchaseOrderFooter = ({ single, children }) => (
  <footer className={`${styles.footer} ${single ? styles.singleFooter : ""}`}>
    {children}
  </footer>
);

export const PurchaseOrderContactModal = ({ contact, onClose, onCall }) => (
  <BottomModal visible={Boolean(contact)} onClose={onClose} showCloseIcon>
    <FlexColumn className="align-items-center p-4">
      <Cross color="#696969" className="close" onClick={onClose} />
      <img src="/logos/po_creator_logo.svg" alt="" />
      <span className="fs-20 fw-700 mt-4">{contact?.name}</span>
      <span className="fs-14 fw-400 text-gray-cool mt-3 mb-4">PO creator</span>
      <Button type="button" onClick={onCall}>
        <span className="fs-13 fw-500 text-white">Call</span>
      </Button>
    </FlexColumn>
  </BottomModal>
);

const SummaryField = ({ icon: Icon, label, value, mapUrl }) => (
  <div className={`d-flex flex-column align-items-center text-center ${styles.summaryItem}`}>
    <Icon size={19} />
    <span className={`fs-11 ${styles.summaryLabel}`}>{label}</span>
    <span className={`fs-12 fw-600 ${styles.summaryValue}`}>{value}</span>
    {mapUrl && (
      <a href={mapUrl} target="_blank" rel="noreferrer" className={styles.summaryMapLink}>Open in map</a>
    )}
  </div>
);

export const PurchaseOrderSummary = ({ purchaseOrder }) => (
  <section className={styles.summaryCard}>
    <SummaryField icon={CalendarDays} label="Deliver by" value={formatDate(purchaseOrder.expected_delivery_date)} />
    <SummaryField
      icon={MapPin}
      label="Deliver at"
      value={`${purchaseOrder.warehouse_code} (${purchaseOrder.warehouse_details.city})` || "—"}
      mapUrl={buildGoogleMapsUrl(purchaseOrder.warehouse_details?.latitude, purchaseOrder.warehouse_details?.longitude )}
    />
    <SummaryField icon={IndianRupee} label="PO amount" value={formatAmount(purchaseOrder.po_amount)} />
  </section>
);

export const PurchaseOrderProductIdentity = ({ item }) => (
  <div className={styles.productIdentity}>
    <img
      src={item.product_image || "/logos/po_creator_logo.svg"}
      alt=""
      style={{ width: 54, height: 54, borderRadius: 10, objectFit: "cover" }}
    />
    <div>
      <h3 className={`fs-14 fw-600 ${styles.productName}`}>{item.product_name || "Product"}</h3>
      <p className={`fs-12 text-gray-cool ${styles.productPrice}`}>{formatAmount(item.price_per_unit)}/{item.buy_measurement_unit || "unit"}</p>
    </div>
  </div>
);

export const PurchaseOrderProducts = ({ items }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <section className={styles.productSection}>
      <button type="button" className={styles.productSectionHeader} onClick={() => setExpanded((value) => !value)}>
        <span>
          <span className={styles.productSectionTitle}>Product details</span>
          <span className={styles.skuBadge}>{items.length} SKUs</span>
        </span>
        {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {expanded && (
        <div className={styles.productList}>
          {items.map((item, index) => (
            <article className={styles.productCard} key={item.id || item.product_number || index}>
              <PurchaseOrderProductIdentity item={item} />
              <div className={styles.productMetrics}>
                <div className={`d-flex flex-column align-items-center ${styles.productMetric}`}>
                  <span className="fs-13 fw-700">{Number(item.quantity_ordered || 0).toLocaleString("en-IN")} {item.buy_measurement_unit || ""}</span>
                  <span className={`fs-11 ${styles.metricLabel}`}>Order qty.</span>
                </div>
                <div className={`d-flex flex-column align-items-center ${styles.productMetric}`}>
                  <span className="fs-13 fw-700">{formatAmount(Number(item.quantity_ordered || 0) * Number(item.price_per_unit || 0))}</span>
                  <span className={`fs-11 ${styles.metricLabel}`}>Order amount</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

const SuccessModal = ({ visible, title, onClose }) => visible && (
  <SushiModal visible isCentered showCloseIcon={false} overlayClickClose={false} overlayOpacity={0.68} className={styles.successModal} onClose={onClose}>
    <section className={`text-center ${styles.dialog}`}>
      <img src="/logos/tick_logo.svg" alt="" style={{ display: "block", width: 72, height: 72, margin: "0 auto", objectFit: "contain" }} />
      <h2 className="fs-20 fw-700 text-center" style={{ margin: "18px 0 20px", lineHeight: 1.35 }}>{title}</h2>
      <button type="button" className="fw-600 text-red-500" style={{ border: 0, padding: 8, background: "transparent" }} onClick={onClose}>Okay</button>
    </section>
  </SushiModal>
);

export const PurchaseOrderSuccessModal = SuccessModal;

export const PurchaseOrderConfirmationModal = ({ visible, purchaseOrder, items, totalWeight, submitting, onBack, onConfirm }) => visible && (
  <BottomModal visible showCloseIcon={false} overlayClickClose={!submitting} overlayOpacity={0.68} className={styles.confirmationModal} onClose={onBack}>
    <section className={styles.bottomSheet}>
      <div className={styles.confirmationContent}>
        <img src="/logos/info_line.png" alt="" style={{ display: "block", width: 72, height: 72, margin: "0 auto", objectFit: "contain" }} />
        <h2 className="fs-20 fw-700 text-center" style={{ margin: "18px 0 20px", lineHeight: 1.35 }}>Confirm PO details</h2>
        <div className={styles.confirmationCard}>
          {[
            ["Delivery location", purchaseOrder.warehouse_details?.name || purchaseOrder.warehouse_code || "—"],
            ["Expected delivery date", formatDate(purchaseOrder.expected_delivery_date)],
            ["SKUs", items.length],
            ["Net weight", formatWeight(totalWeight)],
            ["PO amount", formatAmount(purchaseOrder.po_amount)],
          ].map(([label, value]) => (
            <div className={styles.confirmationRow} key={label}>
              {label}
              <span className={styles.confirmationValue}>{value}</span>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.confirmationFooter}>
        <div className={styles.caution}>
          <img src="/logos/caution.png" width="24" height="24" alt="" />
          <span>Once confirmed, quantity, price, and expected delivery date will be locked.</span>
        </div>
        <div className={styles.dialogActions}>
          <PurchaseOrderButton type="button" outline disabled={submitting} onClick={onBack}>Back</PurchaseOrderButton>
          <PurchaseOrderButton type="button" disabled={submitting} onClick={onConfirm}>{submitting ? "Confirming..." : "Confirm"}</PurchaseOrderButton>
        </div>
      </div>
    </section>
  </BottomModal>
);
